<template>
    <teleport to="body">
        <transition name="notes-scrim">
            <div v-if="modelValue" class="notes-scrim" @click.self="close">
                <transition name="notes-panel">
                    <aside v-if="modelValue" class="notes-panel" role="dialog" aria-modal="true">
                        <header class="notes-panel-header">
                            <div>
                                <h2 class="notes-panel-title">{{ t('supportNotes.drawerTitle') }}</h2>
                                <p class="notes-panel-subtitle">{{ subtitle }}</p>
                            </div>
                            <button type="button" class="notes-close" :aria-label="t('supportNotes.cancel')" @click="close">
                                <i class="mdi mdi-close" aria-hidden="true"></i>
                            </button>
                        </header>

                        <div class="notes-filter-bar">
                            <input
                                v-model="searchTerm"
                                class="notes-search"
                                type="text"
                                :placeholder="t('supportNotes.searchPlaceholder')"
                                @input="onSearchInput"
                            />
                            <div class="notes-filter-chips">
                                <button type="button" class="notes-chip" :class="{ 'notes-chip--active': activeFilter === 'all' }" @click="setFilter('all')">
                                    {{ t('supportNotes.filter.all') }}
                                </button>
                                <button
                                    v-for="cat in categories"
                                    :key="cat.slug"
                                    type="button"
                                    class="notes-chip"
                                    :class="{ 'notes-chip--active': activeFilter === cat.slug }"
                                    @click="setFilter(cat.slug)"
                                >
                                    {{ cat.label }}
                                </button>
                            </div>
                        </div>

                        <div class="notes-panel-list">
                            <p v-if="notes.length === 0 && !loading" class="notes-empty">{{ t('supportNotes.emptyState') }}</p>
                            <NoteRow v-for="note in notes" :key="note.id" :note="note" />
                            <button v-if="nextCursor" type="button" class="notes-load-more" :disabled="loading" @click="loadMore">
                                {{ t('supportNotes.loadMore') }}
                            </button>
                        </div>

                        <div class="notes-add-bar">
                            <div class="notes-add-category-chips">
                                <button
                                    v-for="cat in categories"
                                    :key="cat.slug"
                                    type="button"
                                    class="notes-chip"
                                    :class="{ 'notes-chip--active': newNoteCategory === cat.slug }"
                                    @click="newNoteCategory = cat.slug"
                                >
                                    {{ cat.label }}
                                </button>
                            </div>
                            <div class="notes-add-row">
                                <input
                                    v-model="newNoteBody"
                                    class="notes-add-input"
                                    type="text"
                                    :placeholder="t('supportNotes.drawerAddPlaceholder')"
                                    @keyup.enter="addNote"
                                />
                                <button type="button" class="notes-add-button" :disabled="newNoteBody.trim().length === 0" @click="addNote">
                                    {{ t('supportNotes.add') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </transition>
            </div>
        </transition>

        <div v-if="showError" class="notes-error-toast" role="alert">{{ errorMessage }}</div>
    </teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import NoteRow from './NoteRow.vue'
import { loadCategories, noteCategories, sortNotes } from './categories'
import { t } from '@/i18n'
import { listNotes, createNote, type SupportNoteV1, type SupportNoteCategory, type ListNotesParams, type CreateNoteBody } from '@/api/supportNotes'

type DrawerFilter = 'all' | SupportNoteCategory

const props = defineProps<{
    modelValue: boolean
    accountId: number
    spaName: string
    // When provided, the drawer lists and creates notes scoped to this vessel.
    vesselId?: number | null
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'notes-changed'): void
}>()

// Server-managed taxonomy, fetched once and shared via the categories module cache.
const categories = noteCategories()

const notes = ref<SupportNoteV1[]>([])
const nextCursor = ref<string | null>(null)
const loading = ref(false)
const searchTerm = ref('')
const activeFilter = ref<DrawerFilter>('all')
const newNoteBody = ref('')
const newNoteCategory = ref<SupportNoteCategory>('general')

/** The default category for a new note: the first (lowest sortOrder) active category. */
function defaultCategory(): SupportNoteCategory {
    return categories.value[0]?.slug ?? 'general'
}

const showError = ref(false)
const errorMessage = ref('')

const subtitle = computed(() => t('supportNotes.drawerSubtitle', { id: props.accountId, spaName: props.spaName }))

let searchDebounce: ReturnType<typeof setTimeout> | undefined

function close() {
    emit('update:modelValue', false)
}

function reportError(message?: string) {
    errorMessage.value = message ?? t('supportNotes.errorToast')
    showError.value = true
    setTimeout(() => {
        showError.value = false
    }, 4000)
}

function buildParams(cursor?: string): ListNotesParams {
    const params: ListNotesParams = {}
    if (props.vesselId !== undefined && props.vesselId !== null) params.vesselId = props.vesselId
    if (searchTerm.value.trim() !== '') params.q = searchTerm.value.trim()
    if (activeFilter.value !== 'all') params.category = activeFilter.value
    if (cursor) params.cursor = cursor
    return params
}

async function loadNotes() {
    loading.value = true
    try {
        const result = await listNotes(props.accountId, buildParams())
        notes.value = sortNotes(result.notes)
        nextCursor.value = result.nextCursor
    } catch (error) {
        reportError(error instanceof Error ? error.message : undefined)
    } finally {
        loading.value = false
    }
}

async function loadMore() {
    if (!nextCursor.value) return
    loading.value = true
    try {
        const result = await listNotes(props.accountId, buildParams(nextCursor.value))
        notes.value = sortNotes([...notes.value, ...result.notes])
        nextCursor.value = result.nextCursor
    } catch (error) {
        reportError(error instanceof Error ? error.message : undefined)
    } finally {
        loading.value = false
    }
}

function setFilter(filter: DrawerFilter) {
    activeFilter.value = filter
    loadNotes()
}

function onSearchInput() {
    if (searchDebounce) clearTimeout(searchDebounce)
    searchDebounce = setTimeout(() => loadNotes(), 300)
}

async function addNote() {
    const body = newNoteBody.value.trim()
    if (body.length === 0) return
    try {
        const createBody: CreateNoteBody = { category: newNoteCategory.value, body }
        if (props.vesselId != null) createBody.vesselId = props.vesselId
        const created = await createNote(props.accountId, createBody)
        notes.value = sortNotes([created, ...notes.value])
        newNoteBody.value = ''
        newNoteCategory.value = defaultCategory()
        emit('notes-changed')
    } catch (error) {
        reportError(error instanceof Error ? error.message : undefined)
    }
}

watch(
    () => props.modelValue,
    (open) => {
        if (open) {
            searchTerm.value = ''
            activeFilter.value = 'all'
            newNoteBody.value = ''
            // Ensure the taxonomy is loaded, then seed the add-bar default category.
            loadCategories()
                .then(() => {
                    newNoteCategory.value = defaultCategory()
                })
                .catch(() => {
                    // Non-fatal: chips just render empty; note listing still works.
                })
            loadNotes()
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.notes-scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 2400;
    display: flex;
    justify-content: flex-end;
}

.notes-panel {
    width: 100%;
    max-width: 520px;
    height: 100%;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.notes-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid rgb(210, 219, 224);
}

.notes-panel-title {
    font-size: 18px;
    font-weight: 700;
    color: rgb(35, 94, 150);
    margin: 0;
}

.notes-panel-subtitle {
    font-size: 13px;
    color: rgb(146, 148, 156);
    margin: 2px 0 0;
}

.notes-close {
    background: none;
    border: none;
    cursor: pointer;
    color: rgb(146, 148, 156);
    font-size: 22px;
    line-height: 1;
}

.notes-filter-bar {
    padding: 12px 16px;
    border-bottom: 1px solid rgb(210, 219, 224);
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.notes-search {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid rgb(210, 219, 224);
    border-radius: 8px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: rgb(35, 94, 150);
    }
}

.notes-filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.notes-chip {
    border: 1px solid rgb(210, 219, 224);
    background: #ffffff;
    color: rgb(84, 84, 84);
    border-radius: 14px;
    padding: 3px 12px;
    font-size: 12px;
    cursor: pointer;

    &--active {
        background: rgb(35, 94, 150);
        border-color: rgb(35, 94, 150);
        color: #ffffff;
    }
}

.notes-panel-list {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 0 16px;
}

.notes-empty {
    color: rgb(146, 148, 156);
    font-size: 14px;
    padding: 24px 0;
    text-align: center;
}

.notes-load-more {
    width: 100%;
    background: none;
    border: none;
    color: rgb(35, 94, 150);
    font-size: 13px;
    padding: 12px 0;
    cursor: pointer;
}

.notes-add-bar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid rgb(210, 219, 224);
}

.notes-add-category-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.notes-add-row {
    display: flex;
    gap: 8px;
}

.notes-add-input {
    flex: 1 1 auto;
    padding: 8px 12px;
    border: 1px solid rgb(210, 219, 224);
    border-radius: 8px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: rgb(35, 94, 150);
    }
}

.notes-add-button {
    background: rgb(35, 94, 150);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        background: rgba(35, 94, 150, 0.4);
        cursor: default;
    }

    &:active:not(:disabled) {
        background: rgb(31, 83, 132);
    }
}

.notes-panel-enter-active {
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.notes-panel-leave-active {
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.notes-panel-enter-from,
.notes-panel-leave-to {
    transform: translateX(100%);
}

.notes-scrim-enter-active,
.notes-scrim-leave-active {
    transition: opacity 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.notes-scrim-enter-from,
.notes-scrim-leave-to {
    opacity: 0;
}

.notes-error-toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgb(176, 0, 32);
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    z-index: 2500;
}
</style>
